import React, { useState } from 'react';

import classNames from 'classnames';

import { ExternalLink } from '@uikit';

import { Loading2 } from '@uikit/icons/Loading2';

import { RewardPool } from '@shared/api/types/rewardPools';
import { useAuth } from '@shared/common/providers/AuthProvider';
import MarkCheck from '@shared/constants/icons/MarkCheck';
import MarkFail from '@shared/constants/icons/MarkFail';
import { useRewardPoolAccessPoliciesQuery } from '@shared/hooks/query/rewardPools';

import QueryState from '@store:web/components/common/QueryState/QueryState';

import { transformAccessPoliciesToList } from '../RewardPoolsPage/RewardPoolCard/RewardPoolCardWhitelistRequirementsDialog';

interface RewardPoolPageRequirementsProps {
  rewardPool: RewardPool;
}

export const REWARD_POOL_REQUIREMENTS_ANCHOR_ID = 'reward-pool-requirements';

const RewardPoolPageRequirements = ({ rewardPool }: RewardPoolPageRequirementsProps) => {
  const { isAuth } = useAuth();
  const query = useRewardPoolAccessPoliciesQuery(rewardPool.id, {
    enabled: isAuth,
  });

  const [verifyingRequirementId, setVerifyingRequirementId] = useState<string | null>(null);

  const { data, refetch, isFetching } = query;

  const requirements = transformAccessPoliciesToList(data || []);

  const qualifiedCount = requirements.reduce(
    (acc, requirement) => acc + Number(requirement.qualified),
    0,
  );

  const handleVerify = (requirementId: string) => {
    refetch();
    setVerifyingRequirementId(requirementId);
  };

  const hasRequirements = Boolean(requirements.length);

  if (!isAuth) {
    return null;
  }

  return (
    <>
      <div className="flex xl:justify-between max-xl:flex-col gap-4 xl:gap-6 mb-6 xl:mb-8">
        <h2 id={REWARD_POOL_REQUIREMENTS_ANCHOR_ID} className="heading-h2-adaptive">
          Pool Requirements
        </h2>

        <span className="badge badge-lg min-h-10 font-semibold max-md:w-full md:max-w-[22.5rem] xl:max-w-none">
          <MarkCheck className="badge-icon text-[#9A9AAC]" />
          {qualifiedCount} / {requirements.length} Requirements met
        </span>
      </div>

      <div className="mb-6 xl:mb-8">
        {hasRequirements && (
          <ul className="flex flex-col border-t border-[#E7E7EF]" data-qa="reuquirements_list">
            {requirements.map((requirement) => {
              return (
                <li
                  key={requirement.id}
                  className="md:min-h-[4.5rem] flex md:items-center py-5 gap-4 xl:gap-3 max-md:flex-col border-b border-[#E7E7EF]"
                  data-qa={`requirement-id_${requirement.id}`}
                >
                  <p className="flex flex-col gap-1 xl:gap-0.5">
                    <span
                      className={classNames('text-sm font-semibold xl:text-base', {
                        'text-[#65657B]': requirement.qualified,
                        'text-[#09090B]': !requirement.qualified,
                      })}
                    >
                      {requirement.title}
                    </span>

                    {requirement.description && (
                      <span className="text-2xs leading-3 text-[#65657B] xl:text-xs font-medium">
                        {requirement.description}
                      </span>
                    )}
                  </p>

                  <div
                    className={classNames(
                      'flex gap-3 [&>*]:w-full md:[&>*]:w-[10rem] md:ml-auto max-md:flex-col',
                    )}
                  >
                    {requirement.action && (
                      <ExternalLink
                        className={classNames('msq-btn msq-btn-md msq-btn-secondary')}
                        href={requirement.action.href}
                      >
                        {requirement.action.label}
                      </ExternalLink>
                    )}

                    {!requirement.qualified && requirement.action && (
                      <button
                        disabled={isFetching && verifyingRequirementId === requirement.id}
                        onClick={() => handleVerify(requirement.id)}
                        className="msq-btn msq-btn-primary msq-btn-md"
                        data-qa="verify-button"
                      >
                        {isFetching && verifyingRequirementId === requirement.id ? (
                          <Loading2 className="msq-btn-icon msq-btn-icon-spin" />
                        ) : (
                          ' Verify'
                        )}
                      </button>
                    )}

                    {!requirement.qualified && !requirement.action && (
                      <span
                        className="msq-btn msq-btn-md cursor-default text-[#D62E1F] bg-[#FCEEED]"
                        data-qa="not_qualified"
                      >
                        <MarkFail className="msq-btn-icon" />
                      </span>
                    )}

                    {requirement.qualified && (
                      <span
                        className="msq-btn msq-btn-md cursor-default text-[#209734] bg-[#E6FAE9]"
                        data-qa="qualified"
                      >
                        <MarkCheck className="msq-btn-icon" />
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <QueryState
          isEmpty={!requirements.length}
          query={query}
          loading={
            <div className="flex flex-col gap-6">
              {Array.from({ length: 4 }, (_, i) => {
                return <div key={i} className="skeleton-block h-[2.125rem] lg:h-[2.625rem]" />;
              })}
            </div>
          }
        />
      </div>
    </>
  );
};

export default RewardPoolPageRequirements;
